import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { pmApi, PRIORITIES } from '../../lib/pmApi';
import { Siren, Upload, FileText, X, AlertCircle, UserPlus } from 'lucide-react';

const ROLE_LABELS = {
  property_manager: 'Property Manager',
  maintenance_supervisor: 'Maintenance Supervisor',
  emergency: 'Emergency Contact',
  billing: 'Billing Contact',
};

const SubmitRequest = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [props, setProps] = useState([]);
  const [allPropContacts, setAllPropContacts] = useState([]);  // [{property_id, property_name, role, name, email, phone}]
  const [pickedContactKey, setPickedContactKey] = useState('');
  const [form, setForm] = useState({
    property_id: params.get('property') || '',
    unit_number: '',
    tenant_name: '',
    tenant_phone: '',
    issue_description: '',
    priority: 'Normal',
    access_notes: '',
    special_notes: '',
    po_number: '',
    po_notes: '',
    po_file_id: null,
  });
  const [poResolved, setPoResolved] = useState(null);
  const [poFileMeta, setPoFileMeta] = useState(null);
  const [poUploading, setPoUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => { pmApi.properties().then(setProps).catch(() => {}); }, []);
  // Phase 1 PM bug fix — pull the PM's saved property contacts so the form can quick-fill
  // tenant_name / tenant_phone instead of forcing them to retype.
  useEffect(() => {
    pmApi.contacts().then((c) => setAllPropContacts(c?.property_contacts || [])).catch(() => {});
  }, []);

  // Contacts filtered to the currently-selected property
  const contactsForProperty = useMemo(
    () => (form.property_id ? allPropContacts.filter((c) => c.property_id === form.property_id) : []),
    [allPropContacts, form.property_id],
  );

  // Re-resolve PO settings whenever property changes
  useEffect(() => {
    if (!form.property_id) { setPoResolved(null); return; }
    pmApi.poSettingsForProperty(form.property_id)
      .then((r) => {
        setPoResolved(r.resolved);
        if (r.resolved?.default_po_notes && !form.po_notes) {
          setForm((f) => ({ ...f, po_notes: r.resolved.default_po_notes }));
        }
      })
      .catch(() => setPoResolved(null));
  }, [form.property_id]); // eslint-disable-line

  const poRequired = !!poResolved && (poResolved.po_required || poResolved.required_before_scheduling || poResolved.required_before_invoice);
  const poBlocking = !!poResolved && poResolved.po_required; // must be entered on submit
  const allowPmEntry = !poResolved || poResolved.allow_pm_to_enter !== false;

  const onUploadPo = async (file) => {
    setPoUploading(true);
    try {
      const r = await pmApi.uploadPoFile(file);
      setForm((f) => ({ ...f, po_file_id: r.file_id }));
      setPoFileMeta({ filename: r.filename, size: r.size });
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    }
    setPoUploading(false);
  };

  const clearPoFile = () => { setForm((f) => ({ ...f, po_file_id: null })); setPoFileMeta(null); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.property_id) { setErr('Select a property'); return; }
    if (!form.issue_description.trim()) { setErr('Describe the issue'); return; }
    if (poBlocking && !form.po_number.trim()) {
      setErr('A Purchase Order number is required by your company before submitting.');
      return;
    }
    setSubmitting(true);
    setErr('');
    try {
      const r = await pmApi.submitRequest(form);
      navigate(`/pm/work-orders/${r.id}`, { state: { fresh: true } });
    } catch (e2) {
      const det = e2.response?.data?.detail;
      setErr(typeof det === 'object' ? (det.message || JSON.stringify(det)) : (det || e2.message));
    }
    setSubmitting(false);
  };

  const isEmergency = form.priority === 'Emergency';

  return (
    <div data-testid="pm-submit-page" className="p-6 max-w-3xl">
      <div>
        <p className="text-[#D4AF37] text-xs font-bold tracking-[0.3em] mb-1">NEW REQUEST</p>
        <h1 className="font-['Outfit'] text-3xl font-bold text-white">Submit Service Request</h1>
        <p className="text-[#A3A3A3] text-sm mt-1">NexFlow dispatch will be alerted immediately. Emergency requests also notify on-call dispatchers.</p>
      </div>

      <form onSubmit={submit} className="mt-6 bg-[#141414] border border-[#262626] rounded-md p-6 space-y-4">
        {err && <div data-testid="pm-submit-err" className="text-red-400 text-sm bg-red-900/20 border border-red-800 px-3 py-2 rounded">{err}</div>}

        {isEmergency && (
          <div className="bg-[#7F1D1D]/30 border-2 border-[#EF4444] rounded p-3 flex items-center gap-2">
            <Siren className="w-5 h-5 text-[#EF4444]" />
            <p className="text-[#FCA5A5] text-sm">Emergency requests bypass normal queue and alert on-call dispatch immediately.</p>
          </div>
        )}

        <div>
          <label className="text-white text-sm">Property *</label>
          <select
            data-testid="pm-submit-property"
            value={form.property_id}
            onChange={(e) => { setPickedContactKey(''); setForm({ ...form, property_id: e.target.value }); }}
            className="w-full bg-[#0A0A0A] border border-[#262626] rounded p-3 text-white"
          >
            <option value="">Select property…</option>
            {props.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
          </select>
        </div>

        {/* Saved Property Contacts — quick fill (Phase 1 PM bug fix).
            Always visible when a property is selected and has saved contacts; the manual
            tenant fields below stay editable so PMs can still type a new contact. */}
        {form.property_id && contactsForProperty.length > 0 && (
          <div data-testid="pm-submit-saved-contacts" className="bg-[#0A0A0A] border border-[#262626] rounded p-3">
            <div className="flex items-center gap-2 mb-2">
              <UserPlus className="w-4 h-4 text-[#D4AF37]" />
              <p className="text-[#D4AF37] text-[10px] font-bold tracking-[0.22em] uppercase">Quick Fill From Saved Contacts</p>
            </div>
            <select
              data-testid="pm-submit-saved-contact-select"
              value={pickedContactKey}
              onChange={(e) => {
                const key = e.target.value;
                setPickedContactKey(key);
                if (!key) return;
                const c = contactsForProperty[parseInt(key, 10)];
                if (c) {
                  setForm((f) => ({
                    ...f,
                    tenant_name: c.name || f.tenant_name,
                    tenant_phone: c.phone || f.tenant_phone,
                  }));
                }
              }}
              className="w-full bg-[#141414] border border-[#262626] rounded p-3 text-white text-sm"
            >
              <option value="">Use a saved contact (optional)…</option>
              {contactsForProperty.map((c, i) => (
                <option key={`${c.property_id}-${i}`} value={String(i)}>
                  {(c.name || '(no name)')} — {ROLE_LABELS[c.role] || c.role}
                  {c.phone ? ` · ${c.phone}` : ''}
                </option>
              ))}
            </select>
            <p className="text-[#525252] text-[11px] mt-2">Selecting a contact fills the Tenant Name and Phone below — you can still edit them or type a different contact manually.</p>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-white text-sm">Unit Number</label>
            <input data-testid="pm-submit-unit" value={form.unit_number} onChange={(e) => setForm({ ...form, unit_number: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded p-3 text-white" />
          </div>
          <div>
            <label className="text-white text-sm">Priority</label>
            <select data-testid="pm-submit-priority" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded p-3 text-white">
              {PRIORITIES.map((p) => (<option key={p} value={p}>{p}</option>))}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-white text-sm">Tenant Name</label>
            <input data-testid="pm-submit-tenant" value={form.tenant_name} onChange={(e) => setForm({ ...form, tenant_name: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded p-3 text-white" />
          </div>
          <div>
            <label className="text-white text-sm">Tenant Phone</label>
            <input data-testid="pm-submit-tenant-phone" value={form.tenant_phone} onChange={(e) => setForm({ ...form, tenant_phone: e.target.value })} className="w-full bg-[#0A0A0A] border border-[#262626] rounded p-3 text-white" />
          </div>
        </div>

        <div>
          <label className="text-white text-sm">Issue Description *</label>
          <textarea
            data-testid="pm-submit-issue"
            rows={4}
            value={form.issue_description}
            onChange={(e) => setForm({ ...form, issue_description: e.target.value })}
            className="w-full bg-[#0A0A0A] border border-[#262626] rounded p-3 text-white"
            placeholder="What's going on?"
            required
          />
        </div>

        <div>
          <label className="text-white text-sm">Access Instructions</label>
          <textarea
            data-testid="pm-submit-access"
            rows={2}
            value={form.access_notes}
            onChange={(e) => setForm({ ...form, access_notes: e.target.value })}
            className="w-full bg-[#0A0A0A] border border-[#262626] rounded p-3 text-white"
            placeholder="Gate codes, lockbox, key location, hours…"
          />
        </div>

        <div>
          <label className="text-white text-sm">Special Notes</label>
          <textarea
            data-testid="pm-submit-special"
            rows={2}
            value={form.special_notes}
            onChange={(e) => setForm({ ...form, special_notes: e.target.value })}
            className="w-full bg-[#0A0A0A] border border-[#262626] rounded p-3 text-white"
            placeholder="Anything else NexFlow should know"
          />
        </div>

        {/* Purchase Order block — visible whenever PO is required by company/property rules */}
        {poResolved && (poRequired || allowPmEntry) && (
          <div data-testid="pm-submit-po-block" className={`rounded-md p-4 border ${poBlocking ? 'border-[#D4AF37] bg-[#D4AF37]/5' : 'border-[#262626] bg-[#0A0A0A]'}`}>
            <div className="flex items-center justify-between mb-3">
              <p className="text-[#D4AF37] text-[10px] font-bold tracking-[0.22em] uppercase">Purchase Order {poBlocking ? '(Required)' : '(Optional)'}</p>
              {poBlocking && (
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#D4AF37]"><AlertCircle className="w-3 h-3" /> Required</span>
              )}
            </div>
            {!allowPmEntry ? (
              <p className="text-[#A3A3A3] text-sm">Your company has disabled PM-entered POs. NexFlow Admin will attach the PO. Please coordinate with billing if needed.</p>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-white text-sm">PO Number {poBlocking && '*'}</label>
                    <input
                      data-testid="pm-submit-po-number"
                      value={form.po_number}
                      onChange={(e) => setForm({ ...form, po_number: e.target.value })}
                      className={`w-full bg-[#0A0A0A] border rounded p-3 text-white ${poBlocking && !form.po_number.trim() && err ? 'border-[#EF4444]' : 'border-[#262626]'}`}
                      placeholder="e.g. PO-12345"
                      required={poBlocking}
                      aria-invalid={!!(poBlocking && !form.po_number.trim() && err)}
                    />
                    {poBlocking && !form.po_number.trim() && err && (
                      <p role="alert" data-testid="pm-submit-po-number-error" className="text-[#EF4444] text-xs mt-1">PO Number is required by your company.</p>
                    )}
                  </div>
                  <div>
                    <label className="text-white text-sm">PO Document</label>
                    {form.po_file_id ? (
                      <div data-testid="pm-submit-po-file" className="flex items-center gap-2 bg-[#0A0A0A] border border-[#262626] rounded p-3">
                        <FileText className="w-4 h-4 text-[#D4AF37]" />
                        <span className="text-white text-sm flex-1 truncate">{poFileMeta?.filename || 'PO uploaded'}</span>
                        <button type="button" onClick={clearPoFile} className="text-[#A3A3A3] hover:text-red-400"><X className="w-4 h-4" /></button>
                      </div>
                    ) : (
                      <label data-testid="pm-submit-po-upload-label" className="flex items-center gap-2 bg-[#0A0A0A] border border-[#262626] border-dashed rounded p-3 text-[#A3A3A3] hover:border-[#D4AF37] hover:text-[#D4AF37] cursor-pointer">
                        <Upload className="w-4 h-4" />
                        <span className="text-sm">{poUploading ? 'Uploading…' : 'Upload PO (PDF, image, doc)'}</span>
                        <input data-testid="pm-submit-po-upload" type="file" hidden onChange={(e) => e.target.files?.[0] && onUploadPo(e.target.files[0])} />
                      </label>
                    )}
                  </div>
                </div>
                <div className="mt-3">
                  <label className="text-white text-sm">PO Notes</label>
                  <textarea
                    data-testid="pm-submit-po-notes"
                    rows={2}
                    value={form.po_notes}
                    onChange={(e) => setForm({ ...form, po_notes: e.target.value })}
                    className="w-full bg-[#0A0A0A] border border-[#262626] rounded p-3 text-white"
                    placeholder="Anything NexFlow billing should know about this PO"
                  />
                </div>
              </>
            )}
          </div>
        )}

        <button
          data-testid="pm-submit-button"
          disabled={submitting}
          className="w-full h-12 bg-[#D4AF37] text-[#0A0A0A] rounded font-medium hover:bg-[#F3E5AB] disabled:opacity-40"
        >
          {submitting ? 'Submitting…' : 'Submit Service Request'}
        </button>
      </form>
    </div>
  );
};

export default SubmitRequest;
